import mixpanel from 'mixpanel-browser';

/**
 * Product analytics.
 *
 * ── The rule that shapes everything here: no personal data leaves the app ──
 * This is a school system. The screens it renders are lists of children — names,
 * dates of birth, addresses, parents' phone numbers, Aadhaar numbers — and none
 * of that is anybody's analytics data.
 *
 * So there is **no student identifier in any event**, not even a uuid: an
 * opaque id is still a key back to a child, and nothing below needs one. The
 * questions this instrumentation exists to answer are about staff behaviour and
 * business volume — how much was collected, which flows get abandoned, what
 * fails — and every one of them is answerable with counts, amounts and
 * categories.
 *
 * This is also why **autocapture is off**. It sends the text of whatever was
 * clicked, and the library scrubs only credit-card and US-SSN shaped values, so
 * clicking a pupil's name in a register uploaded that name. It produced nothing
 * useful either: `$mp_click` on a CSS selector answers no question a school has.
 * v1 ran with it on; see the note in `../../react-ui/src/main.tsx`.
 *
 * ── The one identifier that is deliberate ──
 * Staff are identified, because "which clerk is stuck" is a real support
 * question and they are employees using a work tool, not children. The
 * distinct_id is the **user id**, not the email v1 used — an email that changes
 * splits a person's history in two.
 */

const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const ENABLED = import.meta.env.VITE_MIXPANEL_ENABLED !== 'false';

/**
 * With no token there is nowhere to send to, and `.env.development` ships an
 * empty one deliberately — a dev machine must not write into the production
 * project. But the guard that implemented it made every `track()` a silent
 * no-op, so an event could be misnamed, carry the wrong properties, or never
 * fire at all and nothing said so until it was live. Production was the only
 * place this instrumentation had ever run.
 *
 * So in development without a token, events go to the console instead. Nothing
 * leaves the machine; the point is only that the call is *visible*. Add a token
 * to `.env.development` to send to a real project instead.
 */
const CONSOLE_ONLY = !TOKEN && import.meta.env.DEV;

let started = false;

/** Console stand-in for a real send. Only ever reached when `CONSOLE_ONLY`. */
function echo(kind: string, name: string, payload?: unknown): void {
  console.info(
    `%canalytics%c ${kind} %c${name}`,
    'background:#4f46e5;color:#fff;padding:0 4px;border-radius:2px',
    'color:#6b7280',
    'color:inherit;font-weight:600',
    payload ?? '',
  );
}

export function initAnalytics(): void {
  if (started) return;
  if (!TOKEN) {
    if (CONSOLE_ONLY) echo('init', 'console-only (no VITE_MIXPANEL_TOKEN)');
    return;
  }
  started = true;

  mixpanel.init(TOKEN, {
    // See the note above. Do not turn this on.
    autocapture: false,

    /* Replay is sampled rather than universal: it is for reproducing a reported
       problem, not for watching every fee transaction in the school. Masking is
       the library default in 2.78 but is pinned so an upgrade cannot silently
       start capturing text, and `record_console` — which defaults to *true* —
       is off, or anything the app logged would be uploaded with it. */
    record_sessions_percent: 10,
    record_console: false,
    record_mask_all_text: true,
    record_mask_all_inputs: true,

    /*
     * ── The URL is not safe to send, and Mixpanel sends it by default ──
     * `$current_url` / `$referrer` are attached to every event automatically.
     * This app puts screen state in the query string — `?student=<uuid>` on the
     * collection desk, `?invoice=<uuid>` on the ledger — so those defaults
     * would ship a key back to a specific child on every event fired while the
     * screen was open. `Screen Viewed` carries the nav id instead, which is
     * what anyone would actually chart.
     *
     * `$initial_referrer` goes too: it is stored once at first visit and then
     * replayed forever, so a single link with an id in it would follow the
     * profile around indefinitely.
     */
    property_blacklist: [
      '$current_url',
      '$referrer',
      '$initial_referrer',
      '$referring_domain',
      '$initial_referring_domain',
    ],

    api_host: 'https://api-eu.mixpanel.com',
    opt_out_tracking_by_default: !ENABLED,
    opt_out_persistence_by_default: !ENABLED,
  });
}

/* ── Identity ─────────────────────────────────────────────────────────────── */

export interface AnalyticsUser {
  /** The user's own id. Stable across an email change, unlike v1's `identify(email)`. */
  id: string;
  role?: string | null;
}

export function identifyUser(user: AnalyticsUser): void {
  if (CONSOLE_ONLY) return echo('identify', user.id, { role: user.role });
  if (!started) return;
  mixpanel.identify(user.id);
  /* Name and email are deliberately *not* set. They add nothing a role does not
     — no report here is per-person — and they would put staff PII in a second
     system for no return. */
  mixpanel.people.set({ role: user.role ?? 'unknown' });
}

/**
 * Clears the identity. Call on sign-out, always.
 *
 * A fee counter is a shared machine. Without this the next clerk's events are
 * attributed to whoever signed in before them: the numbers are wrong, and one
 * person's activity is recorded under another person's name.
 */
export function resetAnalytics(): void {
  if (CONSOLE_ONLY) return echo('reset', 'identity cleared');
  if (!started) return;
  mixpanel.reset();
}

/**
 * Tenant context, attached to every subsequent event.
 *
 * This is the dimension that makes the data usable at all: "collections are
 * down" is not actionable, "collections are down at these three schools" is.
 * Set when the context loads and whenever the title bar switches it.
 */
export function setTenantContext(ctx: {
  organizationId?: string | null;
  schoolId?: string | null;
  academicSessionId?: string | null;
}): void {
  const props = {
    organization_id: ctx.organizationId ?? null,
    school_id: ctx.schoolId ?? null,
    academic_session_id: ctx.academicSessionId ?? null,
    app_version: 'ui-v2',
  };
  if (CONSOLE_ONLY) return echo('register', 'tenant context', props);
  if (!started) return;
  mixpanel.register(props);
}

/* ── Events ───────────────────────────────────────────────────────────────── */

/**
 * How a request failed, not just that it did.
 *
 * `http` is a server that answered and said no — a 422, a 500. The other three
 * were invisible before, and on a school's connection they are the ones that
 * matter: `network` is a dropped line or a blocked request, `unauthorized` is a
 * session that expired mid-task, and `malformed` is a gateway answering with an
 * HTML error page where JSON was expected — the shape the ModSecurity
 * workaround in `services/api.ts` exists for.
 *
 * v1's events carry no `reason`, so a report that filters on it reads ui-v2
 * only. Charts that must span both should group by `status`.
 */
export type RequestFailureReason = 'http' | 'network' | 'unauthorized' | 'malformed';

/**
 * Every event the app may send, with its properties.
 *
 * A closed map rather than free-form strings, because the failure mode of
 * product analytics is a dozen spellings of the same event arriving over a year
 * and nobody being able to chart any of them. Adding an event means adding it
 * here, which is the point at which someone asks what question it answers.
 *
 * Naming: "Noun Verbed", past tense — the thing already happened.
 */
export interface AnalyticsEvents {
  /* ── Session ── */
  'Signed In': { role?: string | null };
  'Signed Out': Record<string, never>;
  /** The counterpart to `Signed In`, and the difference between "staff cannot
      remember the password" and "the server is refusing everyone". `status` is
      the HTTP status, or 0 when the request never reached the server. */
  'Sign In Failed': { status: number };

  /** One clean navigation event, replacing autocapture's pageviews. */
  'Screen Viewed': { screen: string };

  /* ── The money path ────────────────────────────────────────────────────
     The reason this file exists. Amounts are aggregatable; nothing names a
     child or an invoice. */
  'Payment Collected': {
    /** `months` settles several months at once; `invoice` settles one. */
    mode: 'months' | 'invoice';
    amount: number;
    payment_method: string;
    month_count: number;
    /** Ad-hoc fees added at the counter. */
    added_fee_count: number;
    /** Total concession given at the desk, as a positive number. */
    concession_amount: number;
    /** Paid less than was owed — the balance stays outstanding. */
    is_part_payment: boolean;
    /** Paid more than was owed — the surplus is held as an advance. */
    is_advance: boolean;
  };
  'Payment Failed': {
    mode: 'months' | 'invoice';
    /** HTTP status, when the failure came from the server. */
    status?: number;
    /** Whether the attempt carried a collection-time concession. Worth its own
        property: until the backend implements the discount merge these fail
        closed by design, and this is how that shows up as a number rather than
        as a support call. */
    had_concession: boolean;
  };

  /* ── Invoices ── */
  'Invoices Published': { count: number; failed_count: number; source: 'row' | 'bulk' | 'detail' };
  'Invoice Line Added': Record<string, never>;
  'Invoice Line Removed': Record<string, never>;
  'Invoice Ledger Filtered': { by: 'student' | 'status' | 'term' };

  /* ── Transactions ──────────────────────────────────────────────────────
     Which way people narrow the ledger. `month` earns its place: v1 forced a
     month on every request and this port does not, so whether anyone actually
     reaches for it is the question that says whether that was right. */
  'Transaction Ledger Filtered': { by: 'month' | 'class' };

  /* ── Fee setup ── */
  'Fee Structure Created': Record<string, never>;
  'Fee Amounts Saved': { changed_count: number; failed_count: number };
  'Fee Component Added': Record<string, never>;
  'Fee Component Removed': Record<string, never>;
  'Fee Head Saved': { action: 'created' | 'updated' };
  /** The server refused a delete — almost always a head still in use. Tells you
      whether the delete affordance is worth keeping at all. */
  'Fee Head Delete Refused': Record<string, never>;
  'Discount Saved': { action: 'created' | 'updated'; calculation: string };
  'Discount Retired': Record<string, never>;
  /** A student put on a discount. The *kind* of concession the school grants is
      the question worth charting; which child got it is not. */
  'Student Discount Assigned': { calculation: string };
  'Student Discount Removed': Record<string, never>;

  /* ── Expenses ──────────────────────────────────────────────────────────
     Money out, as countable as money in. Amount and method only — a vendor
     name is a business relationship and no chart here needs it. */
  'Expense Saved': {
    action: 'created' | 'updated';
    amount: number;
    payment_method: string;
    status: string;
    /** Scanned bills attached with this save. */
    receipt_count: number;
  };
  'Expense Removed': Record<string, never>;
  'Expense Ledger Filtered': { by: 'category' | 'status' };
  'Expense Category Saved': { action: 'created' | 'updated' };
  'Expense Category Removed': Record<string, never>;

  /* ── Timetable ─────────────────────────────────────────────────────────
     `conflict_count` is the reason this is here. A refused write is how the
     backend reports a teacher double-booked, and until now nothing counted
     them — so how often the builder is fighting the schedule was unknowable. */
  'Timetable Saved': {
    created_count: number;
    updated_count: number;
    cleared_count: number;
    conflict_count: number;
  };
  'Timetable Slot Cleared': Record<string, never>;
  /** How many days one trip through the slot drawer filled. The question this
      answers is whether repeating is worth the control — v1 had no equivalent
      and a full week was forty passes through the form. */
  'Timetable Repeated': { day_count: number };
  /** Whether schools actually run something other than the eight periods v1
      hard-coded. If nobody ever changes it, the default is right. */
  'Timetable Periods Changed': { period_count: number; retimed_count: number };

  /* ── Tasks ─────────────────────────────────────────────────────────────
     v1 could create a task and delete it and nothing in between, so "how much
     of what we assign actually gets finished" was not a question the app could
     answer about itself. `Task Status Changed` is the one event here worth
     having; the rest are for symmetry with the other registers. No title, no
     assignee — who was asked to do what is not a chart. */
  'Task Saved': { action: 'created' | 'updated'; priority: string };
  'Task Status Changed': { from: string; to: string; was_overdue: boolean };
  'Task Removed': Record<string, never>;
  'Task Ledger Filtered': { by: 'status' | 'priority' | 'assignee' };

  /* ── Gate passes ───────────────────────────────────────────────────────
     A register and nothing else — no save, no status change, because the screen
     writes nothing (see services/gatepassApi.ts). Which filter people reach for
     is the one question worth asking of it, and it has a real answer to give:
     if `date` dominates, the screen is being used to ask "who is out today",
     and a default of today's date would save a click on every visit. Printing
     is counted under `Document Printed` with `document: 'gate_pass'`. */
  'Gate Pass Register Filtered': { by: 'student' | 'status' | 'type' | 'date' };

  /* ── Holidays ──────────────────────────────────────────────────────────
     `day_count` is the property worth having: the directives justify the
     auto-copy of start date into end date on the claim that 90% of school
     holidays are single-day events, and that claim has never been checked
     against a real school's calendar. This is how it gets checked.

     `Holiday View Used` answers the other open question. v1 built a calendar
     view and nothing counted whether anyone ever switched to it, so the cost of
     maintaining two views was being paid blind. No holiday name — a school's
     calendar is not a chart. */
  'Holiday Saved': {
    action: 'created' | 'updated';
    type: string;
    applies_to: string;
    day_count: number;
  };
  'Holiday Removed': Record<string, never>;
  'Holiday View Used': { view: 'list' | 'calendar' };

  /* ── First-run setup ────────────────────────────────────────────────────
     Where a new organization stops. The wizard has four steps and only the
     last one leaves the app usable, so the interesting number is not how many
     started it but which step the drop-off is on. */
  'Setup Step Completed': { step: 'session' | 'school' | 'board' };
  'Setup Completed': { board_count: number; class_count: number };

  /* ── Academic sessions ── */
  'Session Saved': { action: 'created' | 'updated' };
  'Session Made Current': Record<string, never>;
  'Academic Term Saved': { action: 'created' | 'updated' };

  /* ── The registers ────────────────────────────────────────
     The money path was instrumented first because it is what the school is
     paid for. Everything below is the rest of the day, and without it the
     usage picture was a fee counter with a dark building around it: the
     answerable question is which of these modules a school actually adopts
     after the first month, which is what decides where the next screen goes.

     Same discipline as above — a child, a member of staff, a parent and a
     vendor are all people, and none of them is named by any property here. */

  /* ── Students ── */
  'Student Saved': { action: 'created' | 'updated' };
  'Student Removed': Record<string, never>;
  /** Whether the guardian phone lookup ever finds anyone. It exists so a second
      child is not typed in from scratch, and if `matched` is almost always
      false the feature is costing a round trip to tell staff nothing. The phone
      number is the input, not the property — a parent's number is not a chart. */
  'Parent Lookup Used': { matched: boolean };

  /* ── Staff ── */
  'Staff Saved': { action: 'created' | 'updated' };
  'Staff Removed': Record<string, never>;

  /* ── Attendance ────────────────────────────────────────
     The most-used screen in a school day had no events at all, so "is the
     register actually being called every morning" was not a question the app
     could answer about itself.

     `days_late` is the one that earns its place: the date is a field on this
     screen, not necessarily today, and a register filled in on Friday for the
     whole week is a different product than one called at 8am. 0 is same-day. */
  'Attendance Saved': {
    register: 'student' | 'staff';
    row_count: number;
    present_count: number;
    absent_count: number;
    days_late: number;
  };
  /** The bulk control. If a whole class is marked present in one press and
      nothing is then unmarked, the register is being rubber-stamped. */
  'Attendance Marked All Present': { row_count: number };

  /* ── Marks and homework ────────────────────────────────────────
     Both fan out to one request per row, so a partial write is normal and the
     saved/failed split is the point — the same shape as `Fee Amounts Saved`. */
  'Marks Saved': { saved_count: number; failed_count: number };
  'Homework Saved': { action: 'created' | 'updated' };
  'Homework Removed': Record<string, never>;
  'Homework Evaluated': { saved_count: number; failed_count: number };

  /* ── Exams ── */
  'Exam Saved': { action: 'created' | 'updated' };
  'Exam Removed': Record<string, never>;
  'Exam Schedule Saved': { action: 'created' | 'updated' };
  /** The datesheet builder, which writes a whole paper list in one pass. A
      refused row is usually a room or an invigilator already booked. */
  'Exam Schedules Created': { created_count: number; failed_count: number };
  'Exam Schedule Removed': Record<string, never>;

  /* ── Noticeboard ────────────────────────────────────────
     `school_wide` and `audience_count` are here because the targeting control
     is the part of this screen the directives call critical, and nothing
     measured whether anyone targets anything. If every notice is school-wide,
     the audience picker is elaborate machinery for an unused case. No title
     — what a school tells its parents is not analytics data. */
  'Notice Saved': { action: 'created' | 'updated'; school_wide: boolean; audience_count: number };
  'Notice Removed': Record<string, never>;

  /* ── Classes and sections ── */
  'Class Saved': { action: 'created' | 'updated' };
  'Class Removed': Record<string, never>;
  'Section Saved': { action: 'created' | 'updated' };
  'Section Removed': Record<string, never>;

  /* ── Schools and organizations ── */
  'School Saved': { action: 'created' | 'updated' };
  'School Removed': Record<string, never>;
  'Organization Saved': { action: 'created' | 'updated' };

  /* ── Designations and subjects ────────────────────────────────────────
     Adoption is a toggle in two places — the organization's subject master
     and the school's own list — and `scope` keeps them one chartable
     question rather than two events that have to be added together by hand. */
  'Designation Saved': { action: 'created' | 'updated' };
  'Designation Activation Changed': { active: boolean };
  'Subject Adoption Changed': { scope: 'organization' | 'school'; adopted: boolean };
  /** `source` distinguishes the allocations register from the shortcut inside
      the timetable builder, which exists only because a subject missing at slot
      time used to mean leaving the screen. Whether that shortcut is used is
      what says whether it was worth building. */
  'Subject Allocated': { source: 'allocations' | 'timetable' };
  'Subject Allocation Removed': Record<string, never>;
  'Subject Allocation Edited': { field: 'teacher' | 'weekly_classes' };

  /* ── Transport ────────────────────────────────────────
     A passenger allocation is money — it is what a transport fee is billed
     from — so it is counted like money. `has_custom_fare` is the question
     worth asking of it: the override exists so one family can be charged
     something other than the stop's fare, and if nobody uses it the resolved
     stop fare could be the only path and the screen loses a control. */
  'Transport Allocation Saved': {
    action: 'created' | 'updated';
    has_custom_fare: boolean;
    is_active: boolean;
  };
  'Transport Allocation Removed': Record<string, never>;
  'Vehicle Saved': { action: 'created' | 'updated' };
  'Vehicle Removed': Record<string, never>;
  'Route Saved': { action: 'created' | 'updated' };
  'Route Removed': Record<string, never>;
  'Route Stop Added': Record<string, never>;
  'Route Stop Removed': Record<string, never>;
  /** Reordering a route is a drag, then a save. `stop_count` says how long the
      routes being reordered actually are. */
  'Route Stops Resequenced': { stop_count: number };
  'Stop Saved': { action: 'created' | 'updated' };
  'Stop Removed': Record<string, never>;

  /* ── Print and export ── */
  'Document Printed': { document: string; row_count?: number };
  /** The browser blocked the popup. v1 failed silently here; now it is countable. */
  'Print Blocked': { document: string };
  /**
   * A register downloaded as a workbook. Same `document` names as the printed
   * sheets on purpose: whether a register is being printed or exported is the
   * question, and it is only answerable if the two events name it the same way.
   */
  'Document Exported': { document: string; row_count?: number };

  /* ── Failures ──────────────────────────────────────────────────────────
     The single most useful non-money signal: which requests fail, where, and
     how often. `endpoint` is templated so ids never reach the property. */
  'Request Failed': {
    endpoint: string;
    method: string;
    /** HTTP status, or 0 when the request never reached the server. */
    status: number;
    reason: RequestFailureReason;
  };
}

export function track<K extends keyof AnalyticsEvents>(
  event: K,
  properties: AnalyticsEvents[K],
): void {
  if (CONSOLE_ONLY) return echo('track', event, properties);
  if (!started) return;
  mixpanel.track(event, properties);
}

/**
 * Collapses a URL to a countable shape: `/api/student-invoices/{id}/publish`.
 *
 * Without this every id becomes its own "endpoint" and the failure report is a
 * list of one-offs. Uuids and numeric ids both become `{id}`; the query string
 * goes entirely, since it carries filter values and sometimes a student id.
 */
export function endpointShape(url: string): string {
  return url
    .split('?')[0]
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/{id}')
    .replace(/\/\d+(?=\/|$)/g, '/{id}');
}
